import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import {
  PlusIcon,
  CalendarIcon,
  BookOpenIcon,
  UserGroupIcon,
  CogIcon,
  XMarkIcon,
  SparklesIcon,
  ChevronRightIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline'
import { Brain } from 'lucide-react'

interface QuickAction {
  name: string
  description: string
  icon: React.ElementType
  href: string
  color: string
  gradient: string
  highlight?: boolean
}

const QuickActions: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const { user } = useAuthStore()
  const navigate = useNavigate()

  const getQuickActions = (): QuickAction[] => {
    if (user?.role === 'teacher') {
      return [
        {
          name: '创建课程',
          description: '添加新的课程',
          icon: PlusIcon,
          href: '/teacher/create-course',
          color: 'bg-blue-500',
          gradient: 'from-blue-500 to-cyan-500'
        },
        {
          name: 'AI助手',
          description: '智能教学助手',
          icon: Brain,
          href: '/teacher/ai',
          color: 'bg-purple-500',
          gradient: 'from-purple-500 to-pink-500',
          highlight: true
        },
        {
          name: '管理预约',
          description: '查看和处理预约',
          icon: CalendarIcon,
          href: '/teacher/bookings',
          color: 'bg-emerald-500',
          gradient: 'from-emerald-500 to-teal-500'
        },
        {
          name: '设置时间',
          description: '安排可用时间',
          icon: CalendarIcon,
          href: '/teacher/schedule',
          color: 'bg-orange-500',
          gradient: 'from-orange-500 to-amber-500'
        },
        {
          name: '个人资料',
          description: '更新个人信息',
          icon: CogIcon,
          href: '/teacher/profile',
          color: 'bg-slate-500',
          gradient: 'from-slate-500 to-slate-600'
        }
      ]
    } else if (user?.role === 'admin') {
      return [
        {
          name: 'AI助手',
          description: '智能管理助手',
          icon: Brain,
          href: '/admin/ai',
          color: 'bg-purple-500',
          gradient: 'from-purple-500 to-pink-500',
          highlight: true
        },
        {
          name: '用户管理',
          description: '管理用户账户',
          icon: UserGroupIcon,
          href: '/admin/users',
          color: 'bg-blue-500',
          gradient: 'from-blue-500 to-indigo-500'
        },
        {
          name: '教师管理',
          description: '管理教师信息',
          icon: UserGroupIcon,
          href: '/admin/teachers',
          color: 'bg-emerald-500',
          gradient: 'from-emerald-500 to-teal-500'
        },
        {
          name: '课程管理',
          description: '管理平台课程',
          icon: BookOpenIcon,
          href: '/admin/courses',
          color: 'bg-orange-500',
          gradient: 'from-orange-500 to-amber-500'
        },
        {
          name: '预约管理',
          description: '查看所有预约',
          icon: CalendarIcon,
          href: '/admin/bookings',
          color: 'bg-slate-500',
          gradient: 'from-slate-500 to-slate-600'
        }
      ]
    } else {
      // 学生快速操作
      return [
        {
          name: 'AI助手',
          description: '智能学习助手',
          icon: Brain,
          href: '/student/ai',
          color: 'bg-purple-500',
          gradient: 'from-purple-500 to-pink-500',
          highlight: true
        },
        {
          name: '浏览课程',
          description: '发现精彩课程',
          icon: BookOpenIcon,
          href: '/courses',
          color: 'bg-blue-500',
          gradient: 'from-blue-500 to-indigo-500'
        },
        {
          name: '我的预约',
          description: '查看学习安排',
          icon: CalendarIcon,
          href: '/student/bookings',
          color: 'bg-emerald-500',
          gradient: 'from-emerald-500 to-teal-500'
        },
        {
          name: '群组课程',
          description: '加入学习群组',
          icon: UserGroupIcon,
          href: '/student/group-courses',
          color: 'bg-orange-500',
          gradient: 'from-orange-500 to-amber-500'
        },
        {
          name: '个人资料',
          description: '管理个人信息',
          icon: CogIcon,
          href: '/student/profile',
          color: 'bg-slate-500',
          gradient: 'from-slate-500 to-slate-600'
        }
      ]
    }
  }

  const handleActionClick = (href: string) => {
    navigate(href)
    setIsOpen(false)
  }

  const actions = getQuickActions()

  return (
    <>
      {/* 快捷操作按钮 - 响应式定位 */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 sm:bottom-6 left-4 sm:left-6 w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl sm:rounded-2xl shadow-lg shadow-indigo-300/50 hover:shadow-xl hover:shadow-indigo-400/50 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 z-40 transition-all duration-300 group"
        style={{ bottom: 'max(env(safe-area-inset-bottom, 0px) + 80px, 80px)' }}
        title="快捷操作"
      >
        <PlusIcon className="w-6 h-6 sm:w-7 sm:h-7 mx-auto transition-transform group-hover:rotate-90 duration-300" />
        {/* 新功能提示点 */}
        {user?.role === 'student' && (
          <span className="absolute -top-1 -right-1 flex h-3 w-3 sm:h-4 sm:w-4">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 sm:h-4 sm:w-4 bg-rose-500 border-2 border-white" />
          </span>
        )}
      </button>

      {/* 快捷操作面板 */}
      {isOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 animate-in fade-in duration-200">
          <div
            className="fixed bottom-32 sm:bottom-24 left-4 sm:left-6 w-[calc(100vw-2rem)] sm:w-80 max-w-80 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden animate-in slide-in-from-bottom-4 zoom-in-95 duration-300"
            style={{ bottom: 'max(env(safe-area-inset-bottom, 0px) + 96px, 96px)' }}
          >
            {/* 头部 */}
            <div className="p-5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white relative overflow-hidden">
              <div className="absolute inset-0 opacity-20">
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/30 rounded-full blur-2xl" />
              </div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <RocketLaunchIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">快捷操作</h3>
                    <p className="text-xs text-white/80">快速访问常用功能</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* 操作列表 */}
            <div className="p-3 max-h-[400px] overflow-y-auto">
              <div className="space-y-1">
                {actions.map((action) => (
                  <button
                    key={action.name}
                    onClick={() => handleActionClick(action.href)}
                    className={`w-full flex items-center p-3 text-left rounded-xl transition-all duration-200 group ${
                      action.highlight
                        ? 'bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100'
                        : 'hover:bg-slate-50'
                    }`}
                  >
                    <div
                      className={`flex-shrink-0 w-11 h-11 bg-gradient-to-br ${action.gradient} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-200`}
                    >
                      <action.icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="ml-3 flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-slate-800">{action.name}</p>
                        {action.highlight && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                            <SparklesIcon className="w-3 h-3" />
                            推荐
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{action.description}</p>
                    </div>
                    <ChevronRightIcon className="w-4 h-4 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-1 transition-all" />
                  </button>
                ))}
              </div>

              {/* 底部提示 */}
              {user?.role === 'student' && (
                <div className="mt-3 p-3 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 rounded-xl border border-indigo-100">
                  <div className="flex items-center gap-2 mb-1">
                    <Brain className="h-4 w-4 text-purple-600" />
                    <p className="text-sm font-bold text-purple-900">AI 助手全新升级</p>
                  </div>
                  <p className="text-xs text-purple-700">智能推荐课程，个性化学习路径规划</p>
                </div>
              )}
            </div>
          </div>

          {/* 点击外部关闭 */}
          <div className="absolute inset-0 -z-10" onClick={() => setIsOpen(false)} />
        </div>
      )}
    </>
  )
}

export default QuickActions
